import { createFileRoute } from "@tanstack/react-router";
import {
	CompanyDetails,
	LegalPage,
	type LegalSection,
} from "@/components/legal/legal-page";

export const Route = createFileRoute("/terms")({
	component: TermsPage,
	head: () => ({
		meta: [
			{ title: "Terms of Service — Proxi" },
			{
				name: "description",
				content:
					"The terms that apply when customers and runners use Proxi's errand marketplace in Nigeria.",
			},
			{ property: "og:title", content: "Terms of Service — Proxi" },
			{
				property: "og:description",
				content:
					"The terms for using Proxi's customer and runner services in Nigeria.",
			},
			{ property: "og:url", content: "https://myproxi.ng/terms" },
			{ name: "twitter:title", content: "Terms of Service — Proxi" },
			{
				name: "twitter:description",
				content:
					"The terms for using Proxi's customer and runner services in Nigeria.",
			},
		],
		links: [{ rel: "canonical", href: "https://myproxi.ng/terms" }],
	}),
});

const sections: LegalSection[] = [
	{
		id: "about",
		heading: "About these terms",
		body: (
			<>
				<p>
					These Terms of Service govern your access to Proxi's websites,
					customer app, runner app, and related services. By creating an account
					or using Proxi, you agree to these terms.
				</p>
				<CompanyDetails />
			</>
		),
	},
	{
		id: "eligibility",
		heading: "Who may use Proxi",
		body: (
			<>
				<p>
					You must be at least 18 years old, able to enter a binding contract,
					and legally permitted to use the service in Nigeria. You must provide
					accurate information and keep your account secure.
				</p>
				<p>
					You are responsible for activity carried out through your account.
					Tell us promptly if you believe someone else has accessed it.
				</p>
			</>
		),
	},
	{
		id: "marketplace",
		heading: "How the marketplace works",
		body: (
			<>
				<p>
					Proxi connects customers who need errands completed with independent
					runners who choose which requests to accept. Customers describe an
					errand and its requirements; runners may offer or agree to perform it.
				</p>
				<p>
					Unless we expressly state otherwise, Proxi does not itself perform the
					errand. Runners are independent service providers, not employees,
					agents, or representatives of Proxi. Runners decide when and where to
					work, subject to these terms and applicable law.
				</p>
			</>
		),
	},
	{
		id: "errands",
		heading: "Errands and user responsibilities",
		body: (
			<>
				<p>Customers must give complete, accurate, and lawful instructions.</p>
				<ul>
					<li>
						Customers must have the right to request the errand and provide any
						items, access, or information needed to complete it.
					</li>
					<li>
						Runners must perform accepted errands with reasonable care, follow
						the agreed instructions, and communicate material changes promptly.
					</li>
					<li>
						Both parties must use the in-app record and confirmation process
						honestly, including delivery or completion codes.
					</li>
				</ul>
				<p>
					Do not use Proxi for unlawful, dangerous, fraudulent, or abusive
					activity; regulated goods without all required permissions; weapons;
					illegal drugs; stolen property; or any request that risks another
					person's safety or privacy.
				</p>
			</>
		),
	},
	{
		id: "payments",
		heading: "Prices, payments, and protected funds",
		body: (
			<>
				<p>
					The customer and runner agree the price for an errand through Proxi.
					Before work begins, Proxi may require the customer to fund the agreed
					amount, applicable fees, and an expense allowance. Payment processing
					is provided by third-party payment partners.
				</p>
				<p>
					Funds shown as protected or in escrow are held pending completion or
					resolution under the service flow. We release payment after the
					customer confirms completion, a valid completion code is used, or a
					dispute is resolved. This product feature is not a bank account and
					does not pay interest.
				</p>
				<p>
					Any fees, deductions, or payout amounts shown before confirmation form
					part of the transaction. You are responsible for taxes that apply to
					your use of or earnings from Proxi.
				</p>
			</>
		),
	},
	{
		id: "changes-cancellations",
		heading: "Changes, cancellations, and refunds",
		body: (
			<>
				<p>
					If the scope or expected cost changes, the customer and runner must
					agree to the change in Proxi before the runner incurs it. Do not rely
					on an off-platform payment or agreement.
				</p>
				<p>
					Cancellation eligibility and any fee are shown in the app before you
					confirm. Refunds depend on the errand's status, documented expenses,
					work already performed, and the outcome of any dispute. Payment
					provider processing times may apply after we approve a refund.
				</p>
			</>
		),
	},
	{
		id: "disputes-safety",
		heading: "Disputes and safety",
		body: (
			<>
				<p>
					Raise a problem through in-app support as soon as possible and keep
					relevant messages, receipts, photographs, and delivery evidence. We
					may review account and transaction records, ask both parties for
					information, and decide how protected funds should be released or
					refunded.
				</p>
				<p>
					Use your judgment during every errand. In an emergency or immediate
					threat, contact the appropriate emergency service first, then report
					the incident to Proxi when it is safe to do so.
				</p>
			</>
		),
	},
	{
		id: "content-communications",
		heading: "Content and communications",
		body: (
			<>
				<p>
					You retain ownership of content you submit. You give Proxi a
					non-exclusive licence to host, process, display, and share that
					content only as needed to operate, secure, and improve the service,
					resolve disputes, and meet legal obligations.
				</p>
				<p>
					We may send service communications you request or need to use Proxi,
					including verification codes, delivery confirmation codes, security
					alerts, receipts, and errand status updates. These are transactional
					messages, not marketing messages.
				</p>
			</>
		),
	},
	{
		id: "accounts",
		heading: "Account restrictions and closure",
		body: (
			<>
				<p>
					We may limit, suspend, or close an account where reasonably necessary
					to protect users or the service, investigate suspected fraud or
					misconduct, comply with law, or address a material breach of these
					terms. Where appropriate, we will explain the restriction and allow
					you to contact support.
				</p>
				<p>
					You may request account closure through the app or support. Closing an
					account does not cancel unsettled transactions, payment duties,
					disputes, or records we must retain by law.
				</p>
			</>
		),
	},
	{
		id: "availability-liability",
		heading: "Availability and responsibility",
		body: (
			<>
				<p>
					Proxi is in public beta. We work to keep it reliable and secure, but
					we do not guarantee uninterrupted availability or that every errand
					will receive an offer or be completed.
				</p>
				<p>
					To the extent permitted by law, Proxi is not responsible for indirect
					or consequential loss, loss caused by information or instructions you
					provide, or conduct outside the service that we could not reasonably
					control. Nothing in these terms excludes liability that cannot
					lawfully be excluded, including liability for fraud or wilful
					misconduct.
				</p>
			</>
		),
	},
	{
		id: "law-changes",
		heading: "Governing law and changes",
		body: (
			<>
				<p>
					These terms are governed by the laws of the Federal Republic of
					Nigeria. Please contact support first so we can try to resolve a
					dispute. If that is not possible, the courts with jurisdiction in
					Lagos State may hear the matter, subject to any mandatory rights or
					procedures that apply.
				</p>
				<p>
					We may update these terms as Proxi changes or the law requires. We
					will post the revised version here, change the date above, and give
					reasonable notice when a change materially affects your rights.
				</p>
			</>
		),
	},
];

function TermsPage() {
	return (
		<LegalPage
			eyebrow="Legal"
			title="Terms of Service"
			summary="The practical rules that keep Proxi fair and safe for customers and runners."
			lastUpdated="21 August 2026"
			sections={sections}
		/>
	);
}
